import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useToast } from '../../components/Toast';
import { StrokeIcons } from '../../components/icons/GrayIconBox';
import { getAnonymousUserId } from '../../utils/anonymousUser';
import { getApiUrl } from '../../utils/apiConfig';
import { getMetaMatchParams } from '../../utils/analytics';
import { downloadImageFile } from '../../utils/downloadUtils';
import { fetchWithRetry } from '../../utils/fetchWithRetry';
import { trackClassroomBundleEvent, trackFreeTrialStart } from '../../utils/eventTracker';
import { copyToClipboard, shareContent, shareToKakao } from '../../utils/shareUtils';
import { setPendingFreeTrial } from '../../utils/freeTrialPending';
import {
  buildStartTrialMetaPayload,
  createStartTrialEventId,
  trackMetaStartTrialPixel,
} from '../../utils/paymentTracking';
import {
  buildChatHandoffUrl,
  createChatHandoffPayload,
  storeChatHandoff,
} from '../../utils/chatHandoff';
import {
  buildClassPhotoModelPrompt,
  buildClassPhotoSystemPrompt,
  CLASS_PHOTO_DEFAULT_STYLE_ID,
  CLASS_PHOTO_MAX_PHRASE_CHARS,
  CLASS_PHOTO_PHRASE_EXAMPLES,
  CLASS_PHOTO_STYLE_PRESETS,
  getClassPhotoStylePreset,
  normalizeClassPhotoPhrase,
  normalizeClassPhotoStyleId,
} from '../../config/classPhotoEventConfig';
import ClassPhotoTrialGate from '../../components/events/ClassPhotoTrialGate';
import { getTrackingErrorCode } from '../../utils/trackingErrorCode';
import './ClassPhotoEventPage.css';

const MAX_PHOTO_BYTES = 10 * 1024 * 1024;
const ALLOWED_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);
const RESULT_STORAGE_KEY = 'whatquiz_class_photo_event_result';
const DRAFT_STORAGE_KEY = 'whatquiz_class_photo_event_draft';

const createEventId = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `class-photo-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
};

const buildClassPhotoPptHandoffLines = (phrase) => [
  '이 이미지를 표지로 쓰는, 교실에서 다 같이 웃으며 볼 수 있는 우리반 소개 PPT를 만들어주세요.',
  `1. 표지: 이 이미지와 문구 "${phrase}"를 크게 배치`,
  '2. 우리 반 소개 + 담임 선생님 한마디 (따뜻하지만 유머 있게)',
  '3. 우리 반 약속·규칙 4~5개를 재치있는 문장으로 (예: "지각은 몸만, 마음은 미리 등교")',
  '4. 올해 우리 반 목표 3가지 (멋진 목표 1개 + 소소해서 웃긴 목표 2개)',
  '5. 우리 반 밸런스 게임 2장 (예: "급식 먼저 vs 매점 먼저" 같은 학생 참여형 양자택일, 슬라이드마다 질문 2개씩)',
  '6. 우리 반 어워드 예고 1장 (이달의 칭찬왕·웃음 부자상 등 상 이름 4개와 뽑는 방법)',
  '7. 유쾌한 마무리 1장 ("올해 우리 반, 기대해도 좋아요" 느낌)',
  '초·중등 교실에 어울리는 밝고 장난기 있는 말투로, 슬라이드마다 제목과 본문 문구를 비워두지 말고 구체적으로 채워주세요.',
];

const buildClassPhotoWorksheetHandoffLines = () => [
  '이 이미지를 활용해, 학생들이 바로 풀며 웃을 수 있는 아이스브레이킹 활동지 "우리 반 알아가기"를 만들어주세요.',
  '1. 친구 인터뷰 빙고 3x3 (예: "나랑 생일이 같은 달인 친구", "아침에 알람을 3개 이상 맞추는 친구" — 칸마다 친구 이름을 받아 적는 방식)',
  '2. 우리 반 밸런스 게임 5문항 (둘 중 하나 고르고 한 줄 이유 쓰기)',
  '3. "나를 소개하는 3가지" 작성 칸 (진짜 2개 + 거짓 1개를 쓰고 짝이 거짓을 찾는 방식)',
  '4. 짝 인터뷰 질문 3개와 답을 적는 칸',
  '5. 우리 반 어워드 후보 추천 칸 (예: 웃음 부자상, 급식 미식가상 — 추천 이유 한 줄 포함)',
  'A4로 인쇄해 바로 나눠줄 수 있게 제목·짧은 안내문·작성 칸을 갖추고, 문구는 학생들이 피식 웃을 만큼 유쾌하게 써주세요.',
];

const fileToDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = () => reject(new Error('사진을 읽지 못했어요.'));
  reader.readAsDataURL(file);
});

const ClassPhotoEventPage = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { subscriptionStatus, fetchSubscriptionStatus } = useSubscription();
  const { showToast } = useToast();
  const isPro = Boolean(subscriptionStatus?.isPro);
  const fileInputRef = useRef(null);
  const isAnonymousAtMountRef = useRef(!currentUser);
  const sharePreparationRef = useRef(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [styleId, setStyleId] = useState(CLASS_PHOTO_DEFAULT_STYLE_ID);
  const [phrase, setPhrase] = useState('');
  const [instruction, setInstruction] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [resultImage, setResultImage] = useState('');
  const [shareRecord, setShareRecord] = useState(null);
  const [isSharing, setIsSharing] = useState(false);
  const [isSharedResult, setIsSharedResult] = useState(false);
  const [showAccountActions, setShowAccountActions] = useState(false);
  const [accountActionBusy, setAccountActionBusy] = useState('');
  const [manualShareUrl, setManualShareUrl] = useState('');
  const [trialGateIntent, setTrialGateIntent] = useState('');
  const [quotaExhausted, setQuotaExhausted] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [loadingMessage, setLoadingMessage] = useState('');
  const [loadingStep, setLoadingStep] = useState(0);
  const [exampleSlide, setExampleSlide] = useState(0);

  const normalizedPhrase = useMemo(
    () => normalizeClassPhotoPhrase(phrase),
    [phrase],
  );
  const canGenerate = Boolean(photoFile && normalizedPhrase && !isGenerating);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('share_expired') === '1') {
      setNotice('공유된 작품은 만료됐어요. 우리반 사진으로 새 작품을 만들어보세요.');
    }
    trackClassroomBundleEvent('landing_viewed', {
      conceptId: 'class_photo',
      entry: params.get('utm_source') === 'kakao'
        ? 'kakao_share'
        : 'event_page',
      isAnonymous: isAnonymousAtMountRef.current,
      isPro: false,
      purpose: 'classPhoto',
      stage: 'event_entry',
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    const shareId = new URLSearchParams(window.location.search).get('share');
    if (shareId) {
      fetch(getApiUrl(`/api/classroom-bundles/share/${encodeURIComponent(shareId)}`))
        .then(async (response) => {
          const data = await response.json();
          if (!response.ok) throw new Error(data?.detail || '공유 결과를 열지 못했어요.');
          if (cancelled) return;
          setResultImage(data.image_url);
          setPhrase(data.phrase || '');
          setShareRecord({
            ...data,
            share_url: `${window.location.origin}/s/class-photo/${shareId}`,
          });
          setIsSharedResult(true);
          trackClassroomBundleEvent('shared_result_viewed', {
            conceptId: 'class_photo',
            entry: 'kakao_share',
            isAnonymous: isAnonymousAtMountRef.current,
            isPro: false,
            purpose: 'classPhoto',
            referenceAttached: true,
            shareId,
            stage: 'shared_result',
          });
        })
        .catch((shareError) => {
          if (!cancelled) setError(shareError.message);
        });
      return () => {
        cancelled = true;
      };
    }

    try {
      const saved = JSON.parse(sessionStorage.getItem(RESULT_STORAGE_KEY) || 'null');
      if (saved?.image && saved?.phrase) {
        setResultImage(saved.image);
        setPhrase(saved.phrase);
        setStyleId(normalizeClassPhotoStyleId(saved.style));
      } else {
        const draft = JSON.parse(sessionStorage.getItem(DRAFT_STORAGE_KEY) || 'null');
        if (draft?.photo && draft?.phrase) {
          setPhrase(draft.phrase);
          setInstruction(draft.instruction || '');
          setStyleId(normalizeClassPhotoStyleId(draft.style));
          setPhotoPreview(draft.photo);
          fetch(draft.photo)
            .then((response) => response.blob())
            .then((blob) => {
              if (!cancelled) {
                setPhotoFile(new File([blob], 'class-photo.png', {
                  type: blob.type || 'image/png',
                }));
              }
            })
            .catch(() => {});
        }
      }
    } catch (_) {
      sessionStorage.removeItem(RESULT_STORAGE_KEY);
    }
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (resultImage) return undefined;
    // 감속 모션 선호 시 자동 전환 없이 첫 예시를 정지 화면으로 둔다.
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches) {
      return undefined;
    }
    const timer = window.setInterval(() => {
      setExampleSlide((prev) => (prev + 1) % CLASS_PHOTO_STYLE_PRESETS.length);
    }, 3000);
    return () => window.clearInterval(timer);
  }, [resultImage]);

  useEffect(() => {
    if (!isGenerating) return undefined;
    const messages = [
      '사진 속 인물을 살펴보고 있어요',
      '우리반 캐릭터를 그리고 있어요',
      '문구와 장식을 함께 디자인하고 있어요',
      '마지막 디테일을 다듬고 있어요',
    ];
    let index = 0;
    setLoadingMessage(messages[index]);
    setLoadingStep(index);
    const timer = window.setInterval(() => {
      index = Math.min(index + 1, messages.length - 1);
      setLoadingMessage(messages[index]);
      setLoadingStep(index);
    }, 4500);
    return () => window.clearInterval(timer);
  }, [isGenerating]);

  const handlePhoto = (file) => {
    setError('');
    if (!file) return;
    if (!ALLOWED_TYPES.has(file.type)) {
      setError('JPG, PNG, WebP 사진만 올릴 수 있어요.');
      return;
    }
    if (file.size > MAX_PHOTO_BYTES) {
      setError('사진 크기는 10MB 이하로 줄여주세요.');
      return;
    }
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
    setResultImage('');
  };

  const handleGenerate = async () => {
    if (!photoFile) {
      setError('우리반 학급사진을 한 장 올려주세요.');
      fileInputRef.current?.click();
      return;
    }
    if (!normalizedPhrase) {
      setError('사진에 넣을 문구를 적어주세요.');
      return;
    }

    setError('');
    setQuotaExhausted(false);
    setIsGenerating(true);
    const startedAt = Date.now();
    const eventId = createEventId();
    try {
      const referenceImage = await fileToDataUrl(photoFile);
      const token = currentUser ? await currentUser.getIdToken() : null;
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;

      trackClassroomBundleEvent('generation_started', {
        conceptId: 'class_photo',
        entry: 'event_page',
        eventId,
        isAnonymous: !currentUser,
        isPro: false,
        purpose: 'classPhoto',
        referenceAttached: true,
        stage: 'visual_generation',
        variantId: styleId,
      });

      const response = await fetchWithRetry(
        getApiUrl('/api/image-chat/generate'),
        {
          method: 'POST',
          headers,
          body: JSON.stringify({
            prompt: buildClassPhotoModelPrompt({
              instruction,
              phrase: normalizedPhrase,
              styleId,
            }),
            purpose: 'classPhoto',
            systemPrompt: buildClassPhotoSystemPrompt(styleId),
            referenceImages: [referenceImage],
            eventSurface: 'class_photo_event',
            userInitiatedPhotoUpload: true,
            eventVisitorId: getAnonymousUserId(),
            bundleEventId: eventId,
            eventSourceUrl: `${window.location.origin}${window.location.pathname}`,
            ...getMetaMatchParams(),
          }),
        },
        {
          maxRetries: 0,
          timeoutMs: 180000,
        },
      );
      const data = await response.json();
      if (!response.ok || !data?.success || !data?.image_url) {
        const detail = data?.detail;
        const generationError = new Error(
          (detail && typeof detail === 'object' ? detail.message : detail)
          || '이미지를 만들지 못했어요.',
        );
        generationError.code = (
          (detail && typeof detail === 'object' ? (detail.code || detail.error_code) : null)
          || (response.ok ? 'no_image' : null)
        );
        if (!response.ok) generationError.status = response.status;
        throw generationError;
      }
      setResultImage(data.image_url);
      setShareRecord(null);
      setIsSharedResult(false);
      try {
        sessionStorage.setItem(RESULT_STORAGE_KEY, JSON.stringify({
          image: data.image_url,
          phrase: normalizedPhrase,
          style: styleId,
        }));
      } catch (_) {
        // 결과가 브라우저 저장 한도를 넘더라도 현재 화면에서는 계속 사용할 수 있다.
      }
      sessionStorage.removeItem(DRAFT_STORAGE_KEY);
      trackClassroomBundleEvent('generation_completed', {
        artifactCount: 1,
        conceptId: 'class_photo',
        elapsedMs: Date.now() - startedAt,
        entry: 'event_page',
        eventId,
        isAnonymous: !currentUser,
        isPro: false,
        purpose: 'classPhoto',
        referenceAttached: true,
        stage: 'visual_ready',
        variantId: styleId,
      });
    } catch (generationError) {
      const message = generationError?.message || '이미지를 만들지 못했어요.';
      const exhausted = (
        generationError?.code === 'class_photo_event_already_used'
        || (
          !generationError?.code
          && message.includes('무료 결과를 이미 만들었어요')
        )
      );
      setQuotaExhausted(exhausted);
      setError(
        exhausted
          ? (
            currentUser
              ? '로그인 무료 결과까지 만들었어요. 7일 PRO 체험이나 PRO에서 다른 스타일도 계속 만들 수 있어요.'
              : '무료 작품을 만들었어요. 가입하면 다른 스타일로 한 번 더 만들 수 있어요.'
          )
          : message,
      );
      trackClassroomBundleEvent(exhausted ? 'quota_reached' : 'generation_failed', {
        conceptId: 'class_photo',
        elapsedMs: Date.now() - startedAt,
        entry: 'event_page',
        errorCode: exhausted ? 'class_photo_event_already_used' : getTrackingErrorCode(generationError),
        eventId,
        isAnonymous: !currentUser,
        isPro: false,
        purpose: 'classPhoto',
        referenceAttached: true,
        stage: 'visual_generation',
        variantId: styleId,
      });
    } finally {
      setIsGenerating(false);
    }
  };

  function prepareShareRecord(imageData = resultImage, phraseText = normalizedPhrase) {
    if (shareRecord?.share_url && shareRecord?.image_url) {
      return Promise.resolve(shareRecord);
    }
    if (sharePreparationRef.current) return sharePreparationRef.current;
    const promise = (async () => {
      const token = currentUser ? await currentUser.getIdToken() : null;
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;
      const response = await fetch(getApiUrl('/api/classroom-bundles/share'), {
        method: 'POST',
        headers,
        body: JSON.stringify({
          image_data_url: imageData,
          phrase: phraseText,
          style: styleId,
          visitor_id: getAnonymousUserId(),
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data?.detail || '공유 이미지를 준비하지 못했어요.');
      setShareRecord(data);
      return data;
    })();
    sharePreparationRef.current = promise;
    const clearPendingShare = () => {
      if (sharePreparationRef.current === promise) {
        sharePreparationRef.current = null;
      }
    };
    promise.then(clearPendingShare, clearPendingShare);
    return promise;
  }

  const getOrCreateShareRecord = async () => prepareShareRecord();

  const handleKakaoShare = async () => {
    if (!resultImage || isSharing) return;
    setIsSharing(true);
    setError('');
    try {
      const shared = await getOrCreateShareRecord();
      const shareData = {
        title: normalizedPhrase || '우리반 AI 프사',
        text: '우리반 사진과 원하는 문구로 만든 AI 캐릭터 작품이에요.',
        url: shared.share_url,
        imageUrl: shared.image_url,
        buttonTitle: '우리반도 만들기',
      };
      let result = await shareToKakao(shareData);
      if (!result.success) result = await shareContent(shareData);
      if (!result.success && result.method !== 'cancelled') {
        setManualShareUrl(shared.share_url);
        throw new Error('카카오 공유를 열지 못했어요. 아래 링크 복사를 이용해주세요.');
      }
      if (result.success) {
        showToast(
          result.method === 'kakao'
            ? '카카오톡 공유 화면을 열었어요.'
            : '공유 링크를 복사했어요.',
          'success',
          5000,
        );
        trackClassroomBundleEvent('share_clicked', {
          conceptId: 'class_photo',
          entry: result.method || 'unknown',
          isAnonymous: !currentUser,
          isPro: false,
          purpose: 'classPhoto',
          referenceAttached: true,
          shareId: shared.share_id,
          stage: 'visual_ready',
        });
      }
    } catch (shareError) {
      setError(shareError.message || '공유 화면을 열지 못했어요.');
    } finally {
      setIsSharing(false);
    }
  };

  const handleCopyShareLink = async () => {
    if (!resultImage || isSharing) return;
    setIsSharing(true);
    setError('');
    try {
      const shared = await getOrCreateShareRecord();
      const copied = await copyToClipboard(shared.share_url);
      setManualShareUrl(copied ? '' : shared.share_url);
      if (copied) {
        showToast('공유 링크를 복사했어요. 카카오톡에 붙여넣어 보내보세요.', 'success', 5000);
        trackClassroomBundleEvent('share_clicked', {
          conceptId: 'class_photo',
          entry: 'clipboard',
          isAnonymous: !currentUser,
          isPro,
          purpose: 'classPhoto',
          referenceAttached: true,
          shareId: shared.share_id,
          stage: 'visual_ready',
        });
      } else {
        setError('자동 복사가 막혔어요. 아래 주소를 길게 눌러 복사해주세요.');
      }
    } catch (copyError) {
      setError(copyError.message || '공유 링크를 준비하지 못했어요.');
    } finally {
      setIsSharing(false);
    }
  };

  const handleStartOwn = () => {
    trackClassroomBundleEvent('recipient_started', {
      conceptId: 'class_photo',
      entry: isSharedResult ? 'shared_result' : 'result_retry',
      isAnonymous: !currentUser,
      isPro: false,
      purpose: 'classPhoto',
      shareId: shareRecord?.share_id,
      stage: 'event_entry',
    });
    setPhotoFile(null);
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhotoPreview('');
    setStyleId(CLASS_PHOTO_DEFAULT_STYLE_ID);
    setPhrase('');
    setInstruction('');
    setResultImage('');
    setShareRecord(null);
    setManualShareUrl('');
    setIsSharedResult(false);
    setError('');
    sessionStorage.removeItem(RESULT_STORAGE_KEY);
    sessionStorage.removeItem(DRAFT_STORAGE_KEY);
    window.history.replaceState({}, '', '/events/class-photo');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const requireAccount = (intent = 'continue') => {
    if (currentUser) return true;
    try {
      sessionStorage.setItem('whatquiz_class_photo_event_intent', intent);
    } catch (_) {}
    setPendingFreeTrial({
      triggerFeature: `class_photo_event_${intent}`,
      triggerPage: '/events/class-photo',
      ttlMs: 30 * 60 * 1000,
    });
    navigate(
      `/auth?tab=signup&redirect=${encodeURIComponent('/events/class-photo')}`,
    );
    return false;
  };

  const handleAccountContinue = () => {
    setShowAccountActions(true);
  };

  const openTrialGate = (intent, entry) => {
    trackClassroomBundleEvent('account_gate_viewed', {
      conceptId: 'class_photo',
      entry,
      isAnonymous: !currentUser,
      isPro: false,
      purpose: 'classPhoto',
      stage: 'account_gate',
      targetCategory: intent,
    });
    setTrialGateIntent(intent);
  };

  const handleHdDownload = async () => {
    setAccountActionBusy('download');
    setError('');
    try {
      await downloadImageFile(
        resultImage,
        `whatquiz-class-photo-${Date.now()}.png`,
      );
      trackClassroomBundleEvent('download_clicked', {
        conceptId: 'class_photo',
        entry: 'event_result',
        isAnonymous: !currentUser,
        isPro,
        purpose: 'classPhoto',
        stage: 'visual_ready',
      });
      showToast('사진을 저장했어요.', 'success', 4000);
    } catch (_) {
      setError('이미지를 저장하지 못했어요. 다시 시도해주세요.');
    } finally {
      setAccountActionBusy('');
    }
  };

  const handleTrialGateStart = async () => {
    const intent = trialGateIntent || 'continue';
    if (!currentUser) {
      trackClassroomBundleEvent('signup_clicked', {
        conceptId: 'class_photo',
        entry: 'trial_gate',
        isAnonymous: true,
        isPro: false,
        purpose: 'classPhoto',
        stage: 'account_gate',
        targetCategory: intent,
      });
      try {
        if (photoFile) {
          const photo = await fileToDataUrl(photoFile);
          sessionStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify({
            photo,
            phrase,
            instruction,
            style: styleId,
          }));
        }
      } catch (_) {
        // 큰 사진은 브라우저 저장 한도를 넘을 수 있으며 이 경우 재업로드한다.
      }
      setTrialGateIntent('');
      requireAccount(intent);
      return;
    }

    try {
      const token = await currentUser.getIdToken();
      const eventId = createStartTrialEventId();
      const response = await fetch(getApiUrl('/api/payment/subscription/free-trial'), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(buildStartTrialMetaPayload({
          eventId,
          triggerFeature: `class_photo_event_${intent}`,
          triggerPage: '/events/class-photo',
        })),
      });
      const data = await response.json();
      if (!data?.success) {
        setError('7일 PRO 체험을 이미 사용했다면 PRO에서 계속 이용할 수 있어요.');
        setTrialGateIntent('');
        return;
      }
      trackFreeTrialStart(`class_photo_event_${intent}`);
      trackClassroomBundleEvent('trial_started', {
        conceptId: 'class_photo',
        entry: 'trial_gate',
        isAnonymous: false,
        isPro: true,
        purpose: 'classPhoto',
        stage: 'trial_active',
        targetCategory: intent,
      });
      trackMetaStartTrialPixel({
        contentName: `class_photo_event_${intent}`,
        eventId: data.meta_event_id || eventId,
      });
      await fetchSubscriptionStatus(true);
      showToast('축하해요! 7일 PRO 무료 체험이 시작됐어요.', 'success', 6000);
      setQuotaExhausted(false);
      setError('');
      setTrialGateIntent('');
      setShowAccountActions(true);
      if (intent === 'continue') {
        await handleGenerate();
      } else if (intent === 'download') {
        await handleHdDownload();
      }
    } catch (_) {
      setError('7일 PRO 체험을 시작하지 못했어요. 잠시 후 다시 시도해주세요.');
      setTrialGateIntent('');
    }
  };

  const handleHwpCrossLink = () => {
    trackClassroomBundleEvent('handoff_clicked', {
      conceptId: 'class_photo',
      entry: 'event_result',
      isAnonymous: !currentUser,
      isPro,
      purpose: 'classPhoto',
      stage: 'visual_ready',
      targetCategory: 'hwp_doc',
    });
    navigate('/events/hwp-exam?from=class-photo');
  };

  const handleToolAction = async (targetCategory) => {
    setAccountActionBusy(targetCategory);
    setError('');
    try {
      const shared = await getOrCreateShareRecord();
      const referenceImageUrl = shared.image_url;
      if (!referenceImageUrl) throw new Error('result_upload_failed');

      if (targetCategory === 'image_generation') {
        navigate('/chat', {
          state: {
            classPhotoActivationId: `gallery-${shared.share_id}`,
            classPhotoEventEntry: true,
            category: 'image_generation',
            galleryReference: {
              contentType: 'image',
              sourceData: {
                previews: [referenceImageUrl],
                prompt: `문구 "${normalizedPhrase}"가 들어간 우리반 캐릭터 작품을 수정해주세요.`,
              },
              thumbnailUrl: referenceImageUrl,
              title: normalizedPhrase || '우리반 학급사진',
            },
          },
        });
        return;
      }

      const sourceContent = [
        '[우리반 이미지 문구]',
        normalizedPhrase,
        '',
        `[그림 스타일] ${getClassPhotoStylePreset(styleId).name}`,
        '',
        '[자료 요청]',
        ...(targetCategory === 'ppt_maker'
          ? buildClassPhotoPptHandoffLines(normalizedPhrase)
          : buildClassPhotoWorksheetHandoffLines()),
        '사진만 보고 학생 이름·성격·관계를 추측하지 마세요.',
      ].join('\n');
      const payload = createChatHandoffPayload({
        href: `/chat?category=${targetCategory}`,
        referenceImageUrl,
        referenceMode: 'first_page',
        referenceTitle: normalizedPhrase || '우리반 학급사진',
        referenceTypeLabel: '학급 캐릭터 이미지',
        sourceCategory: 'image_generation',
        sourceContent,
        sourcePurpose: 'classPhoto',
      });
      payload.eventEntry = 'class_photo_event';
      payload.eventResultUrl = referenceImageUrl;
      const handoffId = storeChatHandoff(payload);
      if (handoffId) {
        navigate(buildChatHandoffUrl(targetCategory, handoffId));
      } else {
        navigate('/chat', { state: { chatHandoff: payload } });
      }
    } catch (_) {
      setError('왓퀴즈 수업자료로 이어가지 못했어요. 다시 시도해주세요.');
    } finally {
      setAccountActionBusy('');
    }
  };

  return (
    <main className="class-photo-event">
      <Helmet>
        <title>우리반 AI 프사 만들기 | 왓퀴즈</title>
        <meta
          name="description"
          content="학급사진과 원하는 문구를 넣으면 AI가 문구·장식까지 어우러진 우리반 캐릭터 작품으로 만들어드려요."
        />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <header className="class-photo-event__header">
        <a href="/" aria-label="왓퀴즈 홈">whatquiz</a>
        <span>우리반 AI 프사</span>
      </header>

      {!resultImage && (
        <section className="class-photo-event__hero">
          <h1>우리반만의 작품을<br />바로 만들어보세요</h1>
          <p>인물·문구·장식을 AI가 한 장에 함께 디자인해요.</p>
        </section>
      )}

      {!resultImage && (
        <section
          className="class-photo-event__example"
          aria-label="가상 인물로 만든 4가지 스타일 예시 슬라이드"
        >
          <div className="class-photo-event__example-slides">
            {CLASS_PHOTO_STYLE_PRESETS.map((preset, index) => (
              <img
                key={preset.id}
                src={preset.thumbnail}
                alt={preset.thumbnailAlt}
                loading={index === 0 ? 'eager' : 'lazy'}
                className={index === exampleSlide ? 'active' : ''}
              />
            ))}
          </div>
          <div className="class-photo-event__example-caption" aria-live="off">
            <span>가상 인물 예시</span>
            <strong>{CLASS_PHOTO_STYLE_PRESETS[exampleSlide].name}</strong>
          </div>
        </section>
      )}

      {!resultImage && (
        <section className="class-photo-event__maker" aria-label="우리반 이미지 만들기">
          {notice && <p className="class-photo-event__notice">{notice}</p>}
          <button
            type="button"
            className={`class-photo-event__upload ${photoPreview ? 'has-photo' : ''}`}
            onClick={() => fileInputRef.current?.click()}
          >
            {photoPreview ? (
              <>
                <img src={photoPreview} alt="선택한 학급사진 미리보기" />
                <span>다른 사진 선택</span>
              </>
            ) : (
              <>
                <span className="class-photo-event__upload-icon" aria-hidden="true">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                    <rect x="3" y="3" width="18" height="18" rx="3" />
                    <circle cx="8.5" cy="8.5" r="1.5" />
                    <path d="m21 15-5-5L5 21" />
                  </svg>
                </span>
                <strong>우리반 학급사진 올리기</strong>
                <small>JPG, PNG, WebP · 최대 10MB</small>
              </>
            )}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onClick={(event) => event.stopPropagation()}
            onChange={(event) => {
              handlePhoto(event.target.files?.[0]);
              event.target.value = '';
            }}
            hidden
          />

          <div className="class-photo-event__styles">
            <span className="class-photo-event__styles-label" id="class-photo-style-label">
              그림 스타일
            </span>
            <div
              className="class-photo-event__style-grid"
              role="radiogroup"
              aria-labelledby="class-photo-style-label"
            >
              {CLASS_PHOTO_STYLE_PRESETS.map((preset) => (
                <button
                  key={preset.id}
                  type="button"
                  role="radio"
                  aria-checked={styleId === preset.id}
                  className={`class-photo-event__style ${styleId === preset.id ? 'selected' : ''}`}
                  onClick={() => setStyleId(preset.id)}
                >
                  <span className="class-photo-event__style-thumb">
                    <img src={preset.thumbnail} alt={preset.thumbnailAlt} loading="lazy" />
                    {preset.badge && <em>{preset.badge}</em>}
                    <i aria-hidden="true">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round">
                        <path d="m4.5 12.5 5 5 10-11" />
                      </svg>
                    </i>
                  </span>
                  <strong>{preset.name}</strong>
                </button>
              ))}
            </div>
          </div>

          <div className="class-photo-event__phrase">
            <label htmlFor="class-photo-event-phrase">사진에 넣을 문구</label>
            <input
              id="class-photo-event-phrase"
              value={phrase}
              maxLength={CLASS_PHOTO_MAX_PHRASE_CHARS}
              onChange={(event) => setPhrase(event.target.value)}
              placeholder="예: 우리 반 최고!"
              autoComplete="off"
            />
            <div className="class-photo-event__examples" aria-label="추천 문구">
              {CLASS_PHOTO_PHRASE_EXAMPLES.map((example) => (
                <button
                  key={example.label}
                  type="button"
                  className={phrase === example.phrase ? 'active' : ''}
                  onClick={() => {
                    setPhrase(example.phrase);
                    setInstruction(example.instruction);
                  }}
                >
                  {example.label}
                </button>
              ))}
            </div>
          </div>

          {error && <p className="class-photo-event__error" role="alert">{error}</p>}
          {quotaExhausted && (
            <button
              type="button"
              className="class-photo-event__quota-login"
              onClick={() => openTrialGate('continue', 'quota_exhausted')}
            >
              {currentUser
                ? '7일 PRO로 계속 만들기'
                : '가입하고 다른 스타일 만들기'}
            </button>
          )}

          <button
            type="button"
            className="class-photo-event__generate"
            disabled={!canGenerate}
            onClick={handleGenerate}
          >
            {isGenerating ? loadingMessage : '우리반 작품 만들기'}
          </button>
          {isGenerating && (
            <div className="class-photo-event__ai-loading" role="status" aria-live="polite">
              <div className="class-photo-event__ai-orbit" aria-hidden="true">
                <span className="orbit orbit-one" />
                <span className="orbit orbit-two" />
                <span className="spark spark-one">✦</span>
                <span className="spark spark-two">✦</span>
                <span className="spark spark-three">✦</span>
                <div className="class-photo-event__ai-core">
                  <svg width="34" height="34" viewBox="0 0 24 24" fill="none">
                    <path d="M12 2.5 14.2 9l6.3 2.2-6.3 2.2L12 20l-2.2-6.6-6.3-2.2L9.8 9 12 2.5Z" fill="currentColor" />
                  </svg>
                </div>
              </div>
              <strong>{loadingMessage}</strong>
              <span>사진과 문구를 한 장의 작품으로 만들고 있어요</span>
              <div className="class-photo-event__ai-steps" aria-hidden="true">
                {['인물 분석', '캐릭터 변환', '문구 디자인', '마무리'].map((label, index) => (
                  <i key={label} className={index <= loadingStep ? 'active' : ''}>
                    {label}
                  </i>
                ))}
              </div>
            </div>
          )}
        </section>
      )}

      {resultImage && (
        <section className="class-photo-event__result" aria-live="polite">
          <span>{isSharedResult ? '공유받은 우리반 작품이에요' : '우리반 작품이 완성됐어요'}</span>
          <img src={resultImage} alt={`${normalizedPhrase} 문구가 들어간 우리반 캐릭터 작품`} />
          {error && <p className="class-photo-event__error" role="alert">{error}</p>}
          <div className="class-photo-event__result-actions">
            {isSharedResult ? (
              <>
                <button type="button" className="primary" onClick={handleStartOwn}>나도 만들기</button>
                <button type="button" onClick={handleHdDownload} disabled={accountActionBusy === 'download'}>
                  {accountActionBusy === 'download' ? '저장 중...' : '사진 저장'}
                </button>
              </>
            ) : (
              <>
                <button type="button" className="primary" onClick={handleKakaoShare} disabled={isSharing}>
                  {isSharing ? '공유 준비 중...' : '카카오톡으로 공유'}
                </button>
                <button type="button" onClick={handleHdDownload} disabled={accountActionBusy === 'download'}>
                  {accountActionBusy === 'download' ? '저장 중...' : '사진 저장'}
                </button>
              </>
            )}
          </div>
          <button
            type="button"
            className="class-photo-event__copy-link"
            onClick={handleCopyShareLink}
            disabled={isSharing}
          >
            링크 복사
          </button>
          <button
            type="button"
            className="class-photo-event__copy-link"
            onClick={isSharedResult ? handleKakaoShare : handleStartOwn}
            disabled={isSharing}
          >
            {isSharedResult ? '카카오톡으로 다시 공유' : '다시 만들기'}
          </button>
          {manualShareUrl && (
            <input
              className="class-photo-event__manual-link"
              aria-label="직접 복사할 공유 링크"
              readOnly
              value={manualShareUrl}
              onFocus={(event) => event.target.select()}
            />
          )}
          {!isSharedResult && (
            <div className="class-photo-event__account">
              {!showAccountActions && !isPro ? (
                <button type="button" onClick={handleAccountContinue}>
                  <strong>이 작품으로 더 만들어보기</strong>
                  <span>이미지 수정 · PPT · 활동지</span>
                </button>
              ) : (
                <>
                  {isPro && (
                    <div className="class-photo-event__pro-guide">
                      <strong>PRO로 다음 자료도 바로 만들어보세요</strong>
                      <span>이미지·PPT·활동지를 7일 동안 제한 없이 이용할 수 있어요.</span>
                    </div>
                  )}
                  <div className="class-photo-event__account-actions">
                    <button
                      type="button"
                      disabled={Boolean(accountActionBusy)}
                      onClick={() => handleToolAction('image_generation')}
                    >
                      {accountActionBusy === 'image_generation' ? '연결 중...' : '이미지 수정'}
                    </button>
                    <button
                      type="button"
                      className={isPro ? 'recommended' : ''}
                      disabled={Boolean(accountActionBusy)}
                      onClick={() => handleToolAction('ppt_maker')}
                    >
                      {accountActionBusy === 'ppt_maker' ? '연결 중...' : 'PPT 만들기'}
                    </button>
                    <button
                      type="button"
                      disabled={Boolean(accountActionBusy)}
                      onClick={() => handleToolAction('worksheet_gen')}
                    >
                      {accountActionBusy === 'worksheet_gen' ? '연결 중...' : '활동지 만들기'}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
          <button
            type="button"
            className="class-photo-event__copy-link"
            onClick={handleHwpCrossLink}
          >
            시험지·가정통신문도 한글 파일로 →
          </button>
        </section>
      )}
      <ClassPhotoTrialGate
        isOpen={Boolean(trialGateIntent)}
        intentLabel={trialGateIntent === 'continue' ? '계속 만들기' : '저장하기'}
        onClose={() => setTrialGateIntent('')}
        onStart={handleTrialGateStart}
      />
      {isSharing && (
        <div className="class-photo-event__share-loading" role="status" aria-live="polite">
          <div>
            <span aria-hidden="true">{StrokeIcons.chat}</span>
            <strong>카카오 공유 준비 중</strong>
            <small>생성한 이미지를 카드에 담고 있어요</small>
          </div>
        </div>
      )}
    </main>
  );
};

export default ClassPhotoEventPage;
